import React from 'react'
import { Box, Typography, Button } from '@mui/material'
import { ExternalUrlsLeafEntry } from './types'
import { Table } from 'shared/ui/Table/Table'
import { Column } from 'shared/ui/Table/types'
import { openNodeOnTKatBuilder } from '../utility/TreeNavigation'
import { NodeIdMap } from '../version_mapping/utility'
import {
  createFieldPathColumn,
  createUrlsFoundColumn,
  createTypeColumn,
} from './sharedTableColumns'

/**
 * Displays external URLs found in content tree leaves in a table format.
 * @param externalUrls - Array of leaf entries containing external URL matches
 * @param title - The title to display for the table (e.g., "External Leaf URLs", "Invalid Leaf URLs")
 * @param idMap - Optional node ID map for navigation links
 * @returns Table component showing leaf details and extracted URLs, or null if empty
 */
export function ExternalUrlsLeavesTable({
  externalUrls,
  title = 'External Leaf URLs',
  idMap,
}: {
  externalUrls: ExternalUrlsLeafEntry[]
  title?: string
  idMap?: NodeIdMap
}) {
  if (externalUrls.length === 0) {
    return null
  }

  const hasNavigation = !!idMap

  const columns: Column<ExternalUrlsLeafEntry>[] = [
    {
      title: 'Leaf ID',
      rowContent: ({ leaf_id, leaf_type, parent_id }) => {
        if (!hasNavigation || !idMap || !parent_id) {
          return (
            <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
              {leaf_id}
            </Typography>
          )
        }

        const parentNodeInfo = idMap[parent_id]
        if (!parentNodeInfo) {
          return (
            <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
              {leaf_id}
            </Typography>
          )
        }

        // Create a leaf-like object for navigation
        const leafNode = {
          id: leaf_id,
          tree_id: parentNodeInfo.node.tree_id,
          version: parentNodeInfo.node.version,
          parent_id: parent_id,
          type: leaf_type,
        }

        return (
          <Button
            size="small"
            variant="outlined"
            onClick={() => openNodeOnTKatBuilder(leafNode)}
          >
            Open in TKat
          </Button>
        )
      },
    },
    createTypeColumn<ExternalUrlsLeafEntry>(
      'Leaf Type',
      (item) => item.leaf_type,
    ),
    createFieldPathColumn<ExternalUrlsLeafEntry>(),
    createUrlsFoundColumn<ExternalUrlsLeafEntry>(),
  ]

  return (
    <Box padding="10px">
      <Table
        title={`${title} (${externalUrls.length} ${externalUrls.length !== 1 ? 'leaves' : 'leaf'})`}
        items={externalUrls}
        columns={columns}
      />
    </Box>
  )
}
